import { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../Context/usercontext";
import formatDate from "../../Utils/formateDate";
import {
	FaHeart,
	FaRegHeart,
	FaComment,
	FaShareAlt,
	FaEllipsisH,
	FaBookmark,
	FaRegBookmark,
	FaThumbsDown,
	FaFlag,
	FaTimes
} from "react-icons/fa";
import { FiSend } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import Cookies from "js-cookie";
import UpcomingEvents from "./components/UpcomingEvents";
import AdmissionsOpen from "./components/AdmissionsOpen";
import PostSkeleton from "./components/PostSkeleton";
import CommentModal from "./components/CommentModal";

const Home = () => {
	const [posts, setPosts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [likesData, setLikesData] = useState({});
	const [userLikes, setUserLikes] = useState({});
	const [savedPosts, setSavedPosts] = useState({});
	const [comments, setComments] = useState({});
	const [postComments, setPostComments] = useState([]);
	const [showCommentInputs, setShowCommentInputs] = useState({});
	const [activeCommentPostId, setActiveCommentPostId] = useState(null);
	const [replyToComment, setReplyToComment] = useState(null);
	const [menuOpen, setMenuOpen] = useState(null);
	const { user } = useContext(UserContext);
	const [likingPostIds, setLikingPostIds] = useState([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [imageErrors, setImageErrors] = useState({});
	const [toast, setToast] = useState({ show: false, message: '', type: '' });

	const userId = user?._id;
	const navigate = useNavigate();
	const menuRef = useRef(null);

	// Close menu when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (menuRef.current && !menuRef.current.contains(event.target)) {
				setMenuOpen(null);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	// Close menu on escape key
	useEffect(() => {
		const handleEsc = (e) => {
			if (e.key === 'Escape') {
				setMenuOpen(null);
			}
		};
		window.addEventListener('keydown', handleEsc);
		return () => window.removeEventListener('keydown', handleEsc);
	}, []);

	// Auto-hide toast
	useEffect(() => {
		if (toast.show) {
			const timer = setTimeout(() => {
				setToast({ show: false, message: '', type: '' });
			}, 3000);
			return () => clearTimeout(timer);
		}
	}, [toast]);

	const showToast = (message, type = 'success') => {
		setToast({ show: true, message, type });
	};

	const fetchPosts = async () => {
		try {
			setLoading(true);
			const token = Cookies.get("userToken");
			const response = await axios.get(
				`${process.env.REACT_APP_BACKEND_URL}/apis/admin/getAllPost`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			const postsData = response?.data?.message || [];
			setPosts(postsData);
		} catch (error) {
			console.error("Error fetching posts:", error);
			showToast("Failed to load posts", "error");
		} finally {
			setLoading(false);
		}
	};

	// Fetch all posts
	useEffect(() => {
		fetchPosts();
	}, []);

	const handleCommentSubmit = async (postId) => {
		const commentText = comments[postId];

		if (!commentText?.trim()) return;

		try {
			setIsSubmitting(true);
			setComments(prev => ({
				...prev,
				[postId]: ""
			}));

			const token = Cookies.get("userToken");
			const response = await axios.post(
				`${process.env.REACT_APP_BACKEND_URL}/apis/admin/addComment/${postId}`,
				{ text: commentText },
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			const newComment = response.data?.data?.comment;
			const userId = user._id;
			const fullName = user.fullName;
			newComment.user = {
				_id: userId,
				fullName: fullName,
			};

			// Update UI
			setPostComments((prev) => ({
				...prev,
				[postId]: [newComment, ...(prev[postId] || [])],
			}));

			setComments((prev) => ({
				...prev,
				[postId]: "",
			}));
			setShowCommentInputs((prev) => ({
				...prev,
				[postId]: false,
			}));
			showToast("Comment posted successfully");
		} catch (error) {
			console.error("Error submitting comment:", error);
			showToast("Failed to post comment", "error");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleLikeClick = async (postId) => {
		if (likingPostIds.includes(postId)) return;

		setLikingPostIds((prev) => [...prev, postId]);

		// Optimistic UI update
		setPosts((prevPosts) =>
			prevPosts.map((post) => {
				if (post._id !== postId) return post;

				const alreadyLiked = post.likes.some(
					(like) => like._id === userId
				);

				return {
					...post,
					likes: alreadyLiked
						? post.likes.filter((like) => like._id !== userId)
						: [...post.likes, { _id: userId }],
					likeCount: alreadyLiked
						? post.likeCount - 1
						: post.likeCount + 1,
				};
			})
		);

		try {
			const token = Cookies.get("userToken");
			await axios.post(
				`${process.env.REACT_APP_BACKEND_URL}/apis/admin/toggleLikePost/${postId}`,
				{},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);
		} catch (error) {
			console.error("Like failed, reverting UI", error);

			// Rollback if API fails
			setPosts((prevPosts) =>
				prevPosts.map((post) => {
					if (post._id !== postId) return post;

					const alreadyLiked = post.likes.some(
						(like) => like._id === userId
					);

					return {
						...post,
						likes: alreadyLiked
							? post.likes.filter((like) => like._id !== userId)
							: [...post.likes, { _id: userId }],
						likeCount: alreadyLiked
							? post.likeCount - 1
							: post.likeCount + 1,
					};
				})
			);
			showToast("Failed to like post", "error");
		} finally {
			setLikingPostIds((prev) => prev.filter((id) => id !== postId));
		}
	};


	useEffect(() => {
		if (posts.length > 0 && userId) {
			const initialLikes = {};
			const initialComments = {};

			posts.forEach((post) => {
				const postId = post._id;
				const likesArray = Array.isArray(post.likes) ? post.likes : [];
				const commentsArray = Array.isArray(post.comments) ? post.comments : [];

				const hasUserLiked = likesArray.some(
					(like) => like?._id?.toString() === userId?.toString()
				);

				initialLikes[postId] = hasUserLiked;
				initialComments[postId] = commentsArray;
			});

			setUserLikes(initialLikes);
			setPostComments(initialComments);
		}
	}, [posts, userId]);

	const handleSaveClick = (postId) => {
		setSavedPosts((prev) => ({
			...prev,
			[postId]: !prev[postId],
		}));
		showToast(!savedPosts[postId] ? "Post saved" : "Post removed from saved");
	};

	const handleCommentClick = (postId) => {
		setActiveCommentPostId(postId);
		setReplyToComment(null);
	};

	const handleShareClick = async (post) => {
		const shareData = {
			title: post.adminProfile?.collegeName || "College Post",
			text: post.caption,
			url: `${window.location.origin}/posts/${post._id}`,
		};

		try {
			if (navigator.share) {
				await navigator.share(shareData);
				showToast("Shared successfully");
			} else {
				// Fallback: copy to clipboard
				await navigator.clipboard.writeText(shareData.url);
				showToast("Link copied to clipboard");
			}
		} catch (err) {
			if (err.name !== 'AbortError') {
				showToast("Sharing failed", "error");
			}
		}
	};

	const handleReportPost = (postId) => {
		setMenuOpen(null);
		showToast("Post reported to moderators", "success");
	};

	const handleNotInterested = (postId) => {
		setPosts(posts.filter((p) => p._id !== postId));
		setMenuOpen(null);
		showToast("Post hidden from feed", "success");
	};

	const handleImageError = (postId) => {
		setImageErrors(prev => ({
			...prev,
			[postId]: true
		}));
	};

	// Animation variants
	const postVariants = {
		hidden: { opacity: 0, y: 20 },
		visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
		exit: { opacity: 0, y: -20, transition: { duration: 0.3 } }
	};

	const menuVariants = {
		hidden: { opacity: 0, scale: 0.95, y: -10 },
		visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.2 } },
		exit: { opacity: 0, scale: 0.95, y: -10, transition: { duration: 0.15 } }
	};

	return (
		<div className="min-h-screen bg-stone-50 flex flex-col relative">
			{/* Custom Toast */}
			<AnimatePresence>
				{toast.show && (
					<motion.div
						initial={{ opacity: 0, y: -50 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -50 }}
						className={`fixed top-20 left-1/2 transform -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-lg text-sm font-medium ${toast.type === 'error' ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-green-50 text-green-600 border border-green-200'
							}`}
					>
						{toast.message}
					</motion.div>
				)}
			</AnimatePresence>

			<div className="container mx-auto px-4 py-6 flex-1 flex flex-col lg:flex-row gap-6 overflow-hidden">
				{/* Left Column */}
				<div className="w-full lg:w-3/12 lg:sticky lg:top-6 lg:h-[calc(100vh-80px)] lg:overflow-y-auto scrollbar-hide">
					<UpcomingEvents />
				</div>

				{/* Middle Column */}
				<div className="w-full lg:w-6/12 overflow-y-auto lg:h-[calc(100vh-80px)] lg:px-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
					{loading ? (
						<>
							<PostSkeleton />
							<PostSkeleton />
							<PostSkeleton />
						</>
					) : posts.length > 0 ? (
						<AnimatePresence>
							{posts.map((post) => (
								<motion.div
									key={post._id}
									variants={postVariants}
									initial="hidden"
									animate="visible"
									exit="exit"
									layout
									className="relative bg-white rounded-2xl shadow-sm border border-stone-200/80 overflow-hidden mb-8 transition-all duration-300 hover:shadow-md group"
								>
									{/* Post Header */}
									<div className="flex items-center justify-between p-4 border-b border-stone-100 bg-stone-50/30">
										<div className="flex items-center space-x-3">
											<div className="relative group/avatar">
												<img
													src={
														imageErrors[post._id] 
															? "https://static.vecteezy.com/system/resources/thumbnails/036/594/092/small/man-empty-avatar-photo-placeholder-for-social-networks-resumes-forums-and-dating-sites-male-and-female-no-photo-images-for-unfilled-user-profile-free-vector.jpg"
															: (post.createdBy?.image || post.adminProfile?.image ||
																"https://static.vecteezy.com/system/resources/thumbnails/036/594/092/small/man-empty-avatar-photo-placeholder-for-social-networks-resumes-forums-and-dating-sites-male-and-female-no-photo-images-for-unfilled-user-profile-free-vector.jpg")
													}
													alt="College Logo"
													className="w-11 h-11 rounded-full object-cover border-2 border-white shadow-sm group-hover/avatar:border-amber-200 transition-all duration-300"
													onError={() => handleImageError(post._id)}
												/>
												<div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full shadow-sm"></div>
											</div>
											<div>
												<h3 className="font-bold text-stone-800 leading-tight hover:text-amber-700 transition-colors cursor-pointer">
													{post.adminProfile?.collegeName || "College Admin"}
												</h3>
												<div className="flex items-center text-[11px] font-medium text-stone-400 uppercase tracking-wider mt-0.5">
													<span>{formatDate(post.createdAt)}</span>
													<span className="mx-1.5">•</span>
													<span className="text-amber-600/70">Official</span>
												</div>
											</div>
										</div>

										{/* Post Menu */}
										<div className="relative" ref={menuRef}>
											<button
												onClick={() =>
													setMenuOpen(menuOpen === post._id ? null : post._id)
												}
												className={`p-2 rounded-xl transition-all duration-200 ${menuOpen === post._id
													? "bg-amber-100 text-amber-700 shadow-inner"
													: "text-stone-400 hover:bg-stone-100 hover:text-stone-600"
													}`}
												aria-label="Post options"
											>
												<FaEllipsisH className="text-lg" />
											</button>

											<AnimatePresence>
												{menuOpen === post._id && (
													<motion.div
														variants={menuVariants}
														initial="hidden"
														animate="visible"
														exit="exit"
														className="absolute right-0 mt-2 w-56 bg-white/95 backdrop-blur-md rounded-2xl shadow-lg z-20 border border-stone-200/60 py-2"
													>
														<div className="px-3 py-1 mb-1">
															<p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Options</p>
														</div>

														<button
															onClick={() => { handleSaveClick(post._id); setMenuOpen(null); }}
															className="flex items-center px-4 py-2.5 text-sm font-medium text-stone-600 hover:bg-amber-50 hover:text-amber-700 w-full text-left transition-colors"
														>
															{savedPosts[post._id] ? (
																<FaBookmark className="mr-3 text-amber-600" />
															) : (
																<FaRegBookmark className="mr-3" />
															)}
															{savedPosts[post._id] ? "Saved" : "Save post"}
														</button>

														<button
															onClick={() => { handleShareClick(post); setMenuOpen(null); }}
															className="flex items-center px-4 py-2.5 text-sm font-medium text-stone-600 hover:bg-amber-50 hover:text-amber-700 w-full text-left transition-colors"
														>
															<FaShareAlt className="mr-3" />
															Share
														</button>

														<div className="h-px bg-stone-100 my-1 mx-2"></div>

														<button
															onClick={() => handleNotInterested(post._id)}
															className="flex items-center px-4 py-2.5 text-sm font-medium text-stone-500 hover:bg-stone-50 w-full text-left transition-colors"
														>
															<FaThumbsDown className="mr-3" />
															Not interested
														</button>

														<button
															onClick={() => handleReportPost(post._id)}
															className="flex items-center px-4 py-2.5 text-sm font-bold text-rose-600 hover:bg-rose-50 w-full text-left transition-colors"
														>
															<FaFlag className="mr-3" />
															Report
														</button>
													</motion.div>
												)}
											</AnimatePresence>
										</div>
									</div>

									{/* Post Content */}
									<div className="p-4 pt-2">
										{/* Caption */}
										<p className="text-stone-700 leading-relaxed text-sm md:text-base mb-4 selection:bg-amber-100">
											{post.caption}
										</p>

										{/* Media Container */}
										{post.image && (
											<div className="relative group/media rounded-2xl overflow-hidden bg-stone-100 border border-stone-200 shadow-inner">
												<div className="relative pt-[56.25%] overflow-hidden">
													{/\.(mp4|webm|ogg|wmv)$/i.test(post.image) ? (
														<video
															controls
															className="absolute top-0 left-0 w-full h-full object-cover transition-transform duration-700 group-hover/media:scale-[1.02]"
														>
															<source src={post.image} />
															Your browser does not support video playback.
														</video>
													) : (
														<img
															src={imageErrors[`media-${post._id}`] ? "https://images.unsplash.com/photo-1594122230689-45899d9e6f69?q=80&w=2070&auto=format&fit=crop" : post.image}
															alt="Post content"
															className="absolute top-0 left-0 w-full h-full object-cover transition-transform duration-700 group-hover/media:scale-[1.02]"
															loading="lazy"
															onError={() => setImageErrors(prev => ({ ...prev, [`media-${post._id}`]: true }))}
														/>
													)}

													{/* Subtle Overlay Shadow */}
													<div className="absolute inset-0 pointer-events-none shadow-[inset_0_0_40px_rgba(0,0,0,0.02)]"></div>
												</div>
											</div>
										)}
									</div>

									{/* Post Actions */}
									<div className="px-4 py-3 border-t border-stone-100 bg-stone-50/30">
										<div className="flex justify-between items-center">
											{/* Like Button */}
											<motion.button
												whileTap={{ scale: 0.9 }}
												onClick={() => handleLikeClick(post._id)}
												disabled={likingPostIds.includes(post._id)}
												className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-200 group/like ${post.likes.some(like => like._id === userId)
													? "bg-rose-50 text-rose-600"
													: "text-stone-500 hover:bg-stone-100"
													} disabled:opacity-50`}
											>
												<motion.div
													animate={{ scale: likingPostIds.includes(post._id) ? [1, 1.2, 1] : 1 }}
													transition={{ duration: 0.3 }}
												>
													{post.likes.some(like => like._id === userId) ? (
														<FaHeart className="text-rose-500" />
													) : (
														<FaRegHeart />
													)}
												</motion.div>
												<span className="text-sm font-semibold">
													{post?.likeCount || 0} <span className="hidden sm:inline">Likes</span>
												</span>
											</motion.button>

											{/* Comment Button */}
											<button
												onClick={() => handleCommentClick(post._id)}
												className="flex items-center space-x-2 px-4 py-2 rounded-xl text-stone-500 hover:bg-amber-50 hover:text-amber-700 transition-all duration-200"
											>
												<FaComment className="text-lg" />
												<span className="text-sm font-semibold">Comment</span>
											</button>

											{/* Share Button */}
											<button
												onClick={() => handleShareClick(post)}
												className="flex items-center space-x-2 px-4 py-2 rounded-xl text-stone-500 hover:bg-emerald-50 hover:text-emerald-700 transition-all duration-200"
											>
												<FaShareAlt className="text-lg" />
												<span className="text-sm font-semibold">Share</span>
											</button>
										</div>

										{/* Comment Input Section */}
										{showCommentInputs[post._id] && (
											<motion.div
												initial={{ opacity: 0, y: -10 }}
												animate={{ opacity: 1, y: 0 }}
												exit={{ opacity: 0, y: -10 }}
												className="mt-4 pt-4 border-t border-stone-100"
											>
												<div className="flex items-center space-x-3">
													<div className="w-9 h-9 rounded-full bg-gradient-to-tr from-amber-500 to-amber-600 flex items-center justify-center shadow-sm shrink-0">
														<span className="text-white text-xs font-bold uppercase">
															{user?.fullName?.[0] || "U"}
														</span>
													</div>
													<div className="flex-1 relative group">
														<input
															type="text"
															value={comments[post._id] || ""}
															onChange={(e) =>
																setComments((prev) => ({
																	...prev,
																	[post._id]: e.target.value,
																}))
															}
															onKeyDown={(e) => {
																if (e.key === "Enter" && !e.shiftKey) {
																	e.preventDefault();
																	handleCommentSubmit(post._id);
																}
															}}
															placeholder="Add a friendly comment..."
															className="w-full py-2.5 px-4 bg-stone-100/80 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:bg-white border border-transparent focus:border-amber-200 transition-all placeholder:text-stone-400"
															autoFocus
														/>
														{comments[post._id] && (
															<motion.button
																initial={{ scale: 0 }}
																animate={{ scale: 1 }}
																whileTap={{ scale: 0.9 }}
																onClick={() => handleCommentSubmit(post._id)}
																disabled={isSubmitting}
																className="absolute right-2 top-1.5 p-1.5 bg-amber-600 text-white rounded-xl shadow-md hover:bg-amber-700 transition-colors disabled:opacity-50"
															>
																<FiSend className="text-sm" />
															</motion.button>
														)}
													</div>
												</div>
											</motion.div>
										)}

										{/* Modal */}
										{activeCommentPostId && (
											<CommentModal
												key={activeCommentPostId + (postComments[activeCommentPostId]?.length || 0)}
												comments={postComments[activeCommentPostId]}
												user={user}
												value={comments[activeCommentPostId] || ""}
												onChange={(e) =>
													setComments((prev) => ({
														...prev,
														[activeCommentPostId]: e.target.value,
													}))
												}
												onClose={() => {
													setActiveCommentPostId(null);
													setReplyToComment(null);
												}}
												onSubmit={() =>
													handleCommentSubmit(
														activeCommentPostId,
														replyToComment?._id
													)
												}
												replyTo={replyToComment}
												setReplyTo={setReplyToComment}
												fetchPosts={fetchPosts}
												postId={activeCommentPostId}
												setOptimisticComments={setPostComments}
												isSubmitting={isSubmitting}
												setIsSubmitting={setIsSubmitting}
											/>
										)}
									</div>
								</motion.div>
							))}
						</AnimatePresence>
					) : (
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							className="bg-white rounded-xl shadow-sm p-12 text-center border border-stone-200"
						>
							<p className="text-stone-500 mb-4">No posts available yet</p>
							<button
								onClick={() => window.location.reload()}
								className="text-amber-600 hover:text-amber-700 font-medium hover:underline"
							>
								Refresh feed
							</button>
						</motion.div>
					)}
				</div>

				{/* Right Column */}
				<div className="w-full lg:w-3/12 lg:sticky lg:top-6 lg:h-[calc(100vh-80px)] lg:overflow-y-auto scrollbar-hide">
					<AdmissionsOpen />
				</div>
			</div>
		</div>
	);
};

export default Home;